import ko = require("knockout");
import _ = require("underscore");
import Service = require("./Service");
import FormResponseConditionEditor from "../components/formResponseConditionEditor";
import Logger = require("./Logger");

export interface IFormResponseEditorType {
    MatchesResponseCondition: (formResponseCondition: FormResponseConditionEditor.ObservableFormResponseConditionDto) => boolean;
    PropertiesUsed: string[];
    Value: string;
    SetBaseValues?: (formResponseCondition: FormResponseConditionEditor.ObservableFormResponseConditionDto) => void;
    GetDisplayText: (vm: FormResponseConditionEditor.ObservableFormResponseConditionDto) => string;
    GetDisplayHtml: (vm: FormResponseConditionEditor.ObservableFormResponseConditionDto) => string;
    CanContainChildObjects?: boolean;
    CanContainMultipleChildObjects?: boolean;
    ResetValueTypes?: (formResponseCondition: FormResponseConditionEditor.ObservableFormResponseConditionDto) => void;
    FriendlyName: string;
}

export interface IFormResponseEditorTypes {
    QuestionCondition: IFormResponseEditorType;
    FormSubmitDate: IFormResponseEditorType;
    FormSubmitted: IFormResponseEditorType;
    FormCancelled: IFormResponseEditorType;
    FormWaitlisted: IFormResponseEditorType;
    FormRegistered: IFormResponseEditorType;
    ReservationAttemptSuccessful: IFormResponseEditorType;
    PaymentPromised: IFormResponseEditorType;
    PaymentStatus: IFormResponseEditorType;
    PaymentMethod: IFormResponseEditorType;
    GroupMembership: IFormResponseEditorType;
    CurrentDate: IFormResponseEditorType;
    CurrentPhase: IFormResponseEditorType;
    CurrentPeriod: IFormResponseEditorType;
    And: IFormResponseEditorType;
    Or: IFormResponseEditorType;
    Not: IFormResponseEditorType;
    TextMergeField: IFormResponseEditorType;
    SavedForLater: IFormResponseEditorType;
    ChildFormCount: IFormResponseEditorType;
    ChildFormPercent: IFormResponseEditorType;
}

export class FormResponseConditionEditorConfigurationStatic {

    private static displayDate(dt: string) {
        return Service.formatDateToString(dt);
    }

    private static formatDisplayText(vm: FormResponseConditionEditor.ObservableFormResponseConditionDto, valueType: string, fieldName: string, involvesCollection?: boolean): string {

        if (valueType === "SignatureDate") {
            fieldName = fieldName + " signature date";
            valueType = "DateRange";
        }
        
        if (valueType === "ValueMatch" || valueType === "ChoiceMatch") {
            let collectionModifier = " equals ";
            if (involvesCollection) {
                collectionModifier = " contains ";
            }

            if (vm.TriggerQuestion() && vm.TriggerQuestion().QuestionTypeText === "CourseCrn") {
                if (vm.resolvedCampusSection()) {
                    let term = _.find(vm.AvailableTerms(), x => x.Value === vm.resolvedCampusSection().TermCode) || null;
                    return `${fieldName} ${collectionModifier} crn '${vm.resolvedCampusSection().Crn}' [${vm.resolvedCampusSection().ShortDescription} ${term.Text}]`;
                }
            }
            return `${fieldName} ${collectionModifier} '${vm.ValueMatch()}'`;
        } else if (valueType === "RegexValue") {
            return `${fieldName} ${involvesCollection ? 'contains match' : 'matches'} /${vm.RegexValue()}/`;
        } else if (valueType === "DateRange") {
            let collectionModifier = "";
            if (involvesCollection) {
                collectionModifier = " contains a date that is ";
            }
            if (vm.MinimumDate() && vm.MaximumDate()) {
              
                return fieldName + collectionModifier + " between " + FormResponseConditionEditorConfigurationStatic.displayDate(vm.MinimumDate()) + " and " + FormResponseConditionEditorConfigurationStatic.displayDate(vm.MaximumDate());
            } else if (vm.MinimumDate()) {
                return fieldName + collectionModifier + " >= " + FormResponseConditionEditorConfigurationStatic.displayDate(vm.MinimumDate());
            } else if (vm.MaximumDate()) {
                return fieldName + collectionModifier + " <= " + FormResponseConditionEditorConfigurationStatic.displayDate(vm.MaximumDate());
            }
        } else if (valueType === "NumberRange") {
            let collectionModifier = "";
            if (involvesCollection) {
                collectionModifier = " contains a number that is ";
            }
            if (Service.isNumber(vm.MinimumNumberValue()) && Service.isNumber(vm.MaximumNumberValue())) {
                return fieldName + collectionModifier + " between " + vm.MinimumNumberValue() + " and " + vm.MaximumNumberValue();
            } else if (Service.isNumber(vm.MinimumNumberValue())) {
                return fieldName + collectionModifier + " >= " + vm.MinimumNumberValue();
            } else if (Service.isNumber(vm.MaximumNumberValue())) {
                return fieldName + collectionModifier + " <= " + vm.MaximumNumberValue();
            }
        } else if (valueType === "ReferenceFormMatchRange") {
            let collectionModifier = "";
            if (involvesCollection) {
                collectionModifier = " is ";
            }
            if (Service.isNumber(vm.MinimumReferenceFormMatchCount()) && Service.isNumber(vm.MaximumReferenceFormMatchCount())) {
                return fieldName + collectionModifier + " between " + vm.MinimumReferenceFormMatchCount() + " and " + vm.MaximumReferenceFormMatchCount();
            } else if (Service.isNumber(vm.MinimumReferenceFormMatchCount())) {
                return fieldName + collectionModifier + " >= " + vm.MinimumReferenceFormMatchCount();
            } else if (Service.isNumber(vm.MaximumReferenceFormMatchCount())) {
                return fieldName + collectionModifier + " <= " + vm.MaximumReferenceFormMatchCount();
            }

            if (Service.isNumber(vm.MinimumReferenceFormMatchPercentRaw()) && Service.isNumber(vm.MaximumReferenceFormMatchPercentRaw())) {
                return fieldName + collectionModifier + " between " + vm.MinimumReferenceFormMatchPercentRaw() + "% and " + vm.MaximumReferenceFormMatchPercentRaw()  + "%";
            } else if (Service.isNumber(vm.MinimumReferenceFormMatchPercentRaw())) {
                return fieldName + collectionModifier + " >= " + vm.MinimumReferenceFormMatchPercentRaw() + "%";
            } else if (Service.isNumber(vm.MaximumReferenceFormMatchPercentRaw())) {
                return fieldName + collectionModifier + " <= " + vm.MaximumReferenceFormMatchPercentRaw() + "%";
            }
        } else if (valueType === "DateProximity") {
            if (Service.isNumber(vm.MinutesBeforeAssociatedDate())) {
                let collectionModifier = "";
                if (involvesCollection) {
                    collectionModifier = " any value of ";
                }
                return "Current date is within " + vm.MinutesBeforeAssociatedDateRaw() + " " + vm.MinutesBeforeModifier() + " of " + collectionModifier + fieldName;
            }
        } else if (valueType === "FormStateBooleanMatch") {
            if (vm.ValueMatch() === "true") {
                return "Form is " + fieldName;
            } else if (vm.ValueMatch() === "false") {
                return "Form is not " + fieldName;
            }
        } else if (valueType === "ReservationStateBooleanMatch") {
            if (vm.ValueMatch() === "true") {
                return "Reservation attempt is " + fieldName;
            } else if (vm.ValueMatch() === "false") {
                return "Reservation attempt is not " + fieldName;
            }
        } else if (valueType === "PaymentStateBooleanMatch") {
            if (vm.ValueMatch() === "true") {
                return "Payment is " + fieldName;
            } else if (vm.ValueMatch() === "false") {
                return "Payment is not " + fieldName;
            }
        } else if (valueType === "SignatureSigned") {
            let collectionModifier = "";
             if (involvesCollection) {
                 collectionModifier = " contains a value that ";
             }
            if (vm.SignatureSigned() !== null && vm.SignatureSigned()) {
                return fieldName + collectionModifier + " is signed";
            } else if (vm.SignatureSigned() !== null && vm.SignatureSigned() === false) {
                return fieldName + collectionModifier + " is not signed";
            }
        } else if (valueType === "SignatureSignedBy") {
            let collectionModifier = "";
            if (involvesCollection) {
                collectionModifier = " contains a value that ";
            }
            return fieldName + collectionModifier + " is signed by '" + vm.ValueMatch() + "'";
        } else if (valueType === "HasValue") {
            let modifier = "";
            if (vm.HasValue())
                modifier = "has a value";
            else if (vm.HasValue() === false)
                modifier = "does not have a value";
            return `${fieldName} ${modifier}'`;
        } else if (valueType === "SavedForLaterBooleanMatch") {
            if (vm.ValueMatch() === "true") {
                return "Response is " + fieldName;
            } else if (vm.ValueMatch() === "false") {
                return "Response is not " + fieldName;
            }
        }

        return null;
    }

    public static ResponseEditorTypes: IFormResponseEditorTypes = {
        QuestionCondition: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.QuestionId() != null && formResponseCondition.MembershipProviderId() == null;
            },
            PropertiesUsed: ["QuestionId", "ValueMatch", "RegexValue", "MinimumDate", "MaximumDate", "MinimumNumberValue", "MaximumNumberValue", "SignatureSigned", "SignatureSignedBy", "SignatureDate", "HasValue"],
            Value: "QuestionCondition",
            SetBaseValues(formResponseCondition) {

                var triggerQuestion = formResponseCondition.TriggerQuestion();
                var dateFormat: Service.dateFormatValue = "datetime";
                if (triggerQuestion) {
                    if (Service.stringStartsWith(triggerQuestion.QuestionTypeText, "DateTime")) {
                        if (triggerQuestion.QuestionTypeText === "DateTime_Date") {
                            dateFormat = "date";
                        } else if (triggerQuestion.QuestionTypeText === "DateTime_Time") {
                            dateFormat = "time";
                        }
                        formResponseCondition.ValueMatch(Service.coerceDateValue(formResponseCondition.ValueMatch(), dateFormat));
                        formResponseCondition.MinimumDate(Service.coerceDateValue(formResponseCondition.MinimumDate(), dateFormat));
                        formResponseCondition.MaximumDate(Service.coerceDateValue(formResponseCondition.MaximumDate(), dateFormat));
                    }
                    if (formResponseCondition.ResponseConditionValueTypeText() === "HasValue") {
                        if (formResponseCondition.HasValue() == null || formResponseCondition.HasValue() === undefined)
                            formResponseCondition.HasValue(true);
                    }

                    if (triggerQuestion.QuestionTypeText === "CourseCrn") {
                        let val = formResponseCondition.ValueMatch();
                        if (val) {
                            var values = val.split("|");
                            if (values && values.length === 2) {
                                formResponseCondition.CrnValueMatch(values[0]);
                                formResponseCondition.TermCodeValueMatch(values[1]);
                                return;
                            }
                        }

                        let campus = formResponseCondition.CourseCrnQuestionModel().FormGroupCampus;

                        if (triggerQuestion.SpecificValidationTypeText === "AnyTerm") {
                            campus = formResponseCondition.TriggerQuestion().TypePatternMetadata;
                        }

                        let defaultTerm = formResponseCondition.CourseCrnQuestionModel().TermDefaults[campus];
                        if (!defaultTerm)
                            defaultTerm = formResponseCondition.CourseCrnQuestionModel().TermDefaults["UIUC"];

                        formResponseCondition.TermCodeValueMatch(defaultTerm ? defaultTerm.TermCode : null);
                        return;
                    }
                }
            },
            GetDisplayText(vm) {
                var triggerQuestion = vm.TriggerQuestion();
                if (triggerQuestion) {
                    var valueType = vm.ResponseConditionValueTypeText();
                    var fieldName = "Question [" + triggerQuestion.FieldName + "]";

                    if (triggerQuestion.QuestionTypeText === "Guest") {
                        fieldName += " guest count";
                    }
                    if (triggerQuestion.QuestionTypeText === "MultipleQuestionAnswer") {
                        fieldName += " row count";
                    }

                    return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, valueType, fieldName, triggerQuestion.EmitsCollectionOfValues);
                }
                return "";
            },
            GetDisplayHtml(vm) {
                var triggerQuestion = vm.TriggerQuestion();
                if (triggerQuestion) {
                    var valueType = vm.ResponseConditionValueTypeText();
                    var fieldName = "Question [" + triggerQuestion.FieldName + "]";

                    if (triggerQuestion.QuestionTypeText === "Guest") {
                        fieldName += " guest count";
                    }
                    if (triggerQuestion.QuestionTypeText === "MultipleQuestionAnswer") {
                        fieldName += " row count";
                    }

                    return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, valueType, fieldName, triggerQuestion.EmitsCollectionOfValues);
                }
                return "";
            },
            ResetValueTypes(formResponseCondition) {
                Logger.log("ResetValueTypes called on " + formResponseCondition.getInternalId());
                var propertiesToReset = ["ValueMatch", "RegexValue", "MinimumDate", "MaximumDate", "MinimumNumberValue", "MaximumNumberValue", "SignatureSigned", "SignatureSignedBy", "SignatureDate", "HasValue"];
                var toIgnore = [];
                var valueTypeText = formResponseCondition.ResponseConditionValueTypeText();
                var questionTypeText = formResponseCondition.TriggerQuestion().QuestionTypeText;
                toIgnore.push(valueTypeText);

                if (valueTypeText === "ChoiceMatch" || valueTypeText === "SignatureSignedBy" || (valueTypeText === "HasValue" && questionTypeText === "CheckBox")) {
                    toIgnore.push("ValueMatch");
                }
                if (valueTypeText === "DateRange" || valueTypeText === "SignatureDate") {
                    toIgnore.push("MinimumDate");
                    toIgnore.push("MaximumDate");
                }
                if (valueTypeText === "NumberRange") {
                    toIgnore.push("MinimumNumberValue");
                    toIgnore.push("MaximumNumberValue");
                }
                $.each(propertiesToReset, (index, propertyName) => {
                    if (ko.utils.arrayIndexOf(toIgnore, propertyName) === -1) {
                        var isFunction = (typeof formResponseCondition[propertyName] === "function");
                        if (isFunction) {
                            formResponseCondition[propertyName](null);
                        } else {
                            formResponseCondition[propertyName] = null;
                        }
                    }
                });
            },
            FriendlyName: "Answer Value"
        },

        TextMergeField: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.TextMergeField() !== null && formResponseCondition.TextMergeField() !== "";
            },
            PropertiesUsed: ["TextMergeField", "ValueMatch", "RegexValue", "MinimumDate", "MaximumDate", "MinimumNumberValue", "MaximumNumberValue", "HasValue"],
            Value: "TextMergeField",
            SetBaseValues(formResponseCondition) {
                var tmf = formResponseCondition.SelectedMergeField();
                var dateFormat: Service.dateFormatValue = "datetime";
                if (tmf) {
                    if (tmf.ValueType === "DateTextMergeField" || tmf.ValueType === "TimeTextMergeField") {
                        if (tmf.ValueType === "TimeTextMergeField") {
                            dateFormat = "time";
                        }
                        formResponseCondition.ValueMatch(Service.coerceDateValue(formResponseCondition.ValueMatch(), dateFormat));
                        formResponseCondition.MinimumDate(Service.coerceDateValue(formResponseCondition.MinimumDate(), dateFormat));
                        formResponseCondition.MaximumDate(Service.coerceDateValue(formResponseCondition.MaximumDate(), dateFormat));
                    }
                    if (formResponseCondition.ResponseConditionValueTypeText() === "HasValue") {
                        if (formResponseCondition.HasValue() == null || formResponseCondition.HasValue() === undefined)
                            formResponseCondition.HasValue(true);
                    }
                }
            },
            GetDisplayText(vm) {
                var tmf = vm.SelectedMergeField();
                if (tmf) {
                    var valueType = vm.ResponseConditionValueTypeText();
                    var fieldName = `Text Merge Field [${tmf.Text}]`;

                    return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, valueType, fieldName, tmf.EmitsCollectionOfValues);
                }
                return "";
            },
            GetDisplayHtml(vm) {
                var tmf = vm.SelectedMergeField();
                if (tmf) {
                    var valueType = vm.ResponseConditionValueTypeText();
                    var fieldName = `Text Merge Field [${tmf.Text}]`;

                    return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, valueType, fieldName, tmf.EmitsCollectionOfValues);
                }
                return "";
            },
            ResetValueTypes(formResponseCondition) {
                Logger.log("ResetValueTypes called on " + formResponseCondition.getInternalId());
                var propertiesToReset = ["ValueMatch", "RegexValue", "MinimumDate", "MaximumDate", "MinimumNumberValue", "MaximumNumberValue", "HasValue"];
                var toIgnore = [];
                var valueTypeText = formResponseCondition.ResponseConditionValueTypeText();
                toIgnore.push(valueTypeText);
                if (valueTypeText === "ChoiceMatch") {
                    toIgnore.push("ValueMatch");
                }
                if (valueTypeText === "DateRange") {
                    toIgnore.push("MinimumDate");
                    toIgnore.push("MaximumDate");
                }
                if (valueTypeText === "NumberRange") {
                    toIgnore.push("MinimumNumberValue");
                    toIgnore.push("MaximumNumberValue");
                }
                $.each(propertiesToReset, (index, propertyName) => {
                    if (ko.utils.arrayIndexOf(toIgnore, propertyName) === -1) {
                        var isFunction = (typeof formResponseCondition[propertyName] === "function");
                        if (isFunction) {
                            formResponseCondition[propertyName](null);
                        } else {
                            formResponseCondition[propertyName] = null;
                        }
                    }
                });
            },
            FriendlyName: "Text Merge Field"
        },

        FormSubmitDate: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.SubmitDate();
            },
            PropertiesUsed: ["SubmitDate", "MinimumDate", "MaximumDate", "MinutesBeforeAssociatedDate"],
            Value: "FormSubmitDate",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.SubmitDate(true);
            },
            GetDisplayText(vm) {
                var valueType = "";
                if (vm.ResponseConditionValueTypeText() === 'SubmitDateMinMax') {
                    valueType = "DateRange";
                } else if (vm.ResponseConditionValueTypeText() === 'SubmitDateProximity') {
                    valueType = "DateProximity";
                }
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, valueType, "Form Submit Date");
            },
            GetDisplayHtml(vm) {
                var valueType = "";
                if (vm.ResponseConditionValueTypeText() === 'SubmitDateMinMax') {
                    valueType = "DateRange";
                } else if (vm.ResponseConditionValueTypeText() === 'SubmitDateProximity') {
                    valueType = "DateProximity";
                }
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, valueType, "Form Submit Date");
            },
            FriendlyName: "Form Submit Date"
        },

        FormSubmitted: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.FormSubmitted();
            },
            PropertiesUsed: ["FormSubmitted", "ValueMatch"],
            Value: "FormSubmitted",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.FormSubmitted(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "submitted");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "submitted");
            },
            FriendlyName: "Form Submit State"
        },
        SavedForLater: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.SavedForLater();
            },
            PropertiesUsed: ["SavedForLater", "ValueMatch"],
            Value: "SavedForLater",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.SavedForLater(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "SavedForLaterBooleanMatch", "saved for later");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "SavedForLaterBooleanMatch", "saved for later");
            },
            FriendlyName: "Response Saved For Later"
        },

        FormCancelled: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.FormCancelled();
            },
            PropertiesUsed: ["FormCancelled", "ValueMatch"],
            Value: "FormCancelled",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.FormCancelled(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "cancelled");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "cancelled");
            },
            FriendlyName: "Form Cancelled State"
        },

        FormWaitlisted: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.FormWaitlisted();
            },
            PropertiesUsed: ["FormWaitlisted", "ValueMatch"],
            Value: "FormWaitlisted",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.FormWaitlisted(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "waitlisted");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "waitlisted");
            },
            FriendlyName: "Form Waitlisted State"
        },
        FormRegistered: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.FormRegistered();
            },
            PropertiesUsed: ["FormRegistered", "ValueMatch"],
            Value: "FormRegistered",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.FormRegistered(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "registered");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "FormStateBooleanMatch", "registered");
            },
            FriendlyName: "Form Registered State"
        },
        ReservationAttemptSuccessful: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.ReservationAttemptSuccessful();
            },
            PropertiesUsed: ["ReservationAttemptSuccessful", "ValueMatch"],
            Value: "ReservationAttemptSuccessful",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.ReservationAttemptSuccessful(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ReservationAttemptBooleanMatch", "successful");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ReservationAttemptBooleanMatch", "successful");
            },
            FriendlyName: "Latest Reservation Attempt"
        },

        PaymentPromised: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.PaymentPromised();
            },
            PropertiesUsed: ["PaymentPromised", "ValueMatch"],
            Value: "PaymentPromised",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.PaymentPromised(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "PaymentStateBooleanMatch", "promised");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "PaymentStateBooleanMatch", "promised");
            },
            FriendlyName: "Payment Promised"
        },

        PaymentStatus: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.PaymentStatus();
            },
            PropertiesUsed: ["PaymentStatus", "ValueMatch"],
            Value: "PaymentStatus",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.PaymentStatus(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ValueMatch", "Payment Status");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ValueMatch", "Payment Status");
            },
            FriendlyName: "Payment Status"
        },

        PaymentMethod: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.PaymentMethod();
            },
            PropertiesUsed: ["PaymentMethod", "ValueMatch"],
            Value: "PaymentMethod",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.PaymentMethod(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ValueMatch", "Payment Method");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ValueMatch", "Payment Method");
            },
            FriendlyName: "Payment Method"
        },

        GroupMembership: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.MembershipProviderId() != null;
            },
            PropertiesUsed: ["MembershipProviderId", "ResponseSubmitterIsMember", "ResponseAssociatedUserIsMember", "LastModifiedByIsMember", "QuestionId"],
            Value: "GroupMembership",
            SetBaseValues(formResponseCondition) {
                if (formResponseCondition.ResponseSubmitterIsMember()) {
                    formResponseCondition.GroupMembershipUserType("ResponseSubmitterIsMember");
                }
                else if (formResponseCondition.ResponseAssociatedUserIsMember()) {
                    formResponseCondition.GroupMembershipUserType("ResponseAssociatedUserIsMember");
                }
                else if (formResponseCondition.LastModifiedByIsMember()) {
                    formResponseCondition.GroupMembershipUserType("LastModifiedByIsMember");
                } else {
                    var triggerQuestion = formResponseCondition.TriggerQuestion();
                    if (triggerQuestion) {
                        formResponseCondition.GroupMembershipUserType("NetIdQuestion");
                    }
                }
            },
            GetDisplayText(vm) {
                var groupName = vm.MembershipProviderId();
                var userType = "[unknown]";
                var selectedGroup = vm.SelectedMembershipProvider();
                if (selectedGroup) {
                    groupName = selectedGroup.GroupName;
                }
                if (vm.ResponseSubmitterIsMember() || vm.ResponseAssociatedUserIsMember()) {
                    userType = "Response submitter";
                } else if (vm.LastModifiedByIsMember()) {
                    userType = "Last modifying user";
                } else {
                    var triggerQuestion = vm.TriggerQuestion();
                    if (triggerQuestion) {
                        userType = "Question [" + triggerQuestion.FieldName + "]";
                    }
                }
                return userType + " is a member of " + groupName;
            },
            GetDisplayHtml(vm) {
                var groupName = vm.MembershipProviderId();
                var userType = "[unknown]";
                var selectedGroup = vm.SelectedMembershipProvider();
                if (selectedGroup) {
                    groupName = selectedGroup.GroupName;
                }
                if (vm.ResponseSubmitterIsMember()) {
                    userType = "Response submitter";
                } else if (vm.ResponseAssociatedUserIsMember()) {
                    userType = "Response associated user";
                } else if (vm.LastModifiedByIsMember()) {
                    userType = "Last modifying user";
                } else {
                    var triggerQuestion = vm.TriggerQuestion();
                    if (triggerQuestion) {
                        userType = "Question [" + triggerQuestion.FieldName + "]";
                    }
                }
                return userType + " is a member of " + groupName;
            },
            FriendlyName: "Group Membership"
        },

        CurrentDate: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.CurrentDate();
            },
            PropertiesUsed: ["CurrentDate", "MinimumDate", "MaximumDate"],
            Value: "CurrentDate",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.CurrentDate(true);
            },
            GetDisplayText(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "DateRange", "Current Date");
            },
            GetDisplayHtml(vm) {
                return FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "DateRange", "Current Date");
            },
            FriendlyName: "Current Date"
        },

        CurrentPhase: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.PhaseId() !== null;
            },
            PropertiesUsed: ["PhaseId"],
            Value: "CurrentPhase",
            SetBaseValues() {

            },
            GetDisplayText(vm) {
                var phaseText = vm.PhaseId();
                var selectedPhase = vm.SelectedPhase();
                if (selectedPhase) {
                    phaseText = selectedPhase.PhaseName;
                }
                return "Current Phase is " + phaseText;
            },
            GetDisplayHtml(vm) {
                var phaseText = vm.PhaseId();
                var selectedPhase = vm.SelectedPhase();
                if (selectedPhase) {
                    phaseText = selectedPhase.PhaseName;
                }
                return "Current Phase is " + phaseText;
            },
            FriendlyName: "Current Phase"
        },

        CurrentPeriod: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.PeriodId() !== null;
            },
            PropertiesUsed: ["PeriodId"],
            Value: "CurrentPeriod",
            SetBaseValues() {

            },
            GetDisplayText(vm) {
                var periodText = vm.PeriodId();
                var selectedPeriod = vm.SelectedPeriod();
                if (selectedPeriod) {
                    periodText = selectedPeriod.PeriodName;
                }
                return "Associated Period is " + periodText;
            },
            GetDisplayHtml(vm) {
                var periodText = vm.PeriodId();
                var selectedPeriod = vm.SelectedPeriod();
                if (selectedPeriod) {
                    periodText = selectedPeriod.PeriodName;
                }
                return "Associated Period is " + periodText;
            },
            FriendlyName: "Associated Period"
        },

        And: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.And();
            },
            PropertiesUsed: ["And", "ChildResponseConditions"],
            Value: "And",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.And(true);
            },
            GetDisplayText(vm) {
                var conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayText(element));
                return "(" + conditions.join(" AND ") + ")";
            },
            GetDisplayHtml(vm) {
                var conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayHtml(element));
                return "(" + conditions.join(" <strong>AND</strong> ") + ")";
            },
            CanContainChildObjects: true,
            CanContainMultipleChildObjects: true,
            FriendlyName: "And"
        },

        Or: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.Or();
            },
            PropertiesUsed: ["Or", "ChildResponseConditions"],
            Value: "Or",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.Or(true);
            },
            GetDisplayText(vm) {
                var conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayText(element));
                return "(" + conditions.join(" OR ") + ")";
            },
            GetDisplayHtml(vm) {
                var conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayHtml(element));
                return "(" + conditions.join(" <strong>OR</strong> ") + ")";
            },
            CanContainChildObjects: true,
            CanContainMultipleChildObjects: true,
            FriendlyName: "Or"
        },

        Not: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.Not();
            },
            PropertiesUsed: ["Not", "ChildResponseConditions"],
            Value: "Not",
            SetBaseValues(formResponseCondition) {
                formResponseCondition.Not(true);
                if (formResponseCondition.ChildResponseConditions().length !== 1) {
                    formResponseCondition.ChildResponseConditions([formResponseCondition.createObservableResponseCondition(null)]);
                }
            },
            GetDisplayText(vm) {
                if (vm.ChildResponseConditions().length > 0) {

                    return "NOT (" + vm.ChildResponseConditions()[0].ResponseEditorType().GetDisplayHtml(vm.ChildResponseConditions()[0]) + ")";
                }
                return "";
            },
            GetDisplayHtml(vm) {
                if (vm.ChildResponseConditions().length > 0) {
                    var editorType = vm.ChildResponseConditions()[0].ResponseEditorType();
                    var hideParens = editorType.Value === "Or" || editorType.Value === "And";
                    return "<strong>NOT</strong> " + (hideParens ? "" : "(") + editorType.GetDisplayHtml(vm.ChildResponseConditions()[0]) + (hideParens ? "" : ")");
                }
                return "";
            },
            CanContainChildObjects: true,
            CanContainMultipleChildObjects: false,
            FriendlyName: "Not"
        },

        ChildFormCount: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.ChildAggregate()
                    && formResponseCondition.ChildAggregateReferenceFormId()
                    && (formResponseCondition.MinimumReferenceFormMatchCount() !== null || formResponseCondition.MaximumReferenceFormMatchCount() !== null);
            },
            PropertiesUsed: ["MinimumReferenceFormMatchCount", "MaximumReferenceFormMatchCount", "ChildResponseConditions", "ChildAggregate", "ChildAggregateReferenceFormId"],
            Value: "ChildFormCount",
            SetBaseValues(formResponseCondition) {
                if (formResponseCondition.ChildResponseConditions().length !== 1) {
                    formResponseCondition.ChildResponseConditions([formResponseCondition.createObservableResponseCondition(null)]);
                }
                formResponseCondition.ChildAggregate(true);
            },
            GetDisplayText(vm) {
                if (vm.ChildResponseConditions().length > 0) {
                    let conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayHtml(element));
                    let cf = _.find(vm.AvailableChildForms(), x => x.Id === vm.ChildAggregateReferenceFormId());
                    let str = `Count of child responses of child form '${cf?.FormName || vm.ChildAggregateReferenceFormId()}' `;
                    return `${FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ReferenceFormMatchRange", str, true)} that match the condition (${conditions[0]})`;
                }
            },
            GetDisplayHtml(vm) {
                if (vm.ChildResponseConditions().length > 0) {
                    let conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayHtml(element));
                    let cf = _.find(vm.AvailableChildForms(), x => x.Id === vm.ChildAggregateReferenceFormId());
                    let str = `Count of child responses of child form '${cf?.FormName || vm.ChildAggregateReferenceFormId()}' `;
                    return `${FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ReferenceFormMatchRange", str, true)} that match the condition (${conditions[0]})`;
                }
            },
            CanContainChildObjects: true,
            CanContainMultipleChildObjects: false,
            FriendlyName: "Count of Child Responses"
        },

        ChildFormPercent: {
            MatchesResponseCondition(formResponseCondition) {
                return formResponseCondition.ChildAggregate()
                    && formResponseCondition.ChildAggregateReferenceFormId()
                    && (formResponseCondition.MinimumReferenceFormMatchPercent() !== null || formResponseCondition.MaximumReferenceFormMatchPercent() !== null);
            },
            PropertiesUsed: ["MinimumReferenceFormMatchPercent", "MaximumReferenceFormMatchPercent", "ChildResponseConditions", "ChildAggregate", "ChildAggregateReferenceFormId"],
            Value: "ChildFormPercent",
            SetBaseValues(formResponseCondition) {
                if (formResponseCondition.ChildResponseConditions().length !== 1) {
                    formResponseCondition.ChildResponseConditions([formResponseCondition.createObservableResponseCondition(null)]);
                }
                formResponseCondition.ChildAggregate(true);
            },
            GetDisplayText(vm) {
                if (vm.ChildResponseConditions().length > 0) {
                    let conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayHtml(element));
                    let cf = _.find(vm.AvailableChildForms(), x => x.Id === vm.ChildAggregateReferenceFormId());
                    let str = `Percent of child responses of child form '${cf?.FormName || vm.ChildAggregateReferenceFormId()}' `;
                    return `${FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ReferenceFormMatchRange", str, true)} that match the condition (${conditions[0]})`;
                }
            },
            GetDisplayHtml(vm) {
                if (vm.ChildResponseConditions().length > 0) {
                    let conditions = $.map(vm.ChildResponseConditions(), element => element.ResponseEditorType().GetDisplayHtml(element));
                    let cf = _.find(vm.AvailableChildForms(), x => x.Id === vm.ChildAggregateReferenceFormId());
                    let str = `Percent of child responses of child form '${cf?.FormName || vm.ChildAggregateReferenceFormId()}' `;
                    return `${FormResponseConditionEditorConfigurationStatic.formatDisplayText(vm, "ReferenceFormMatchRange", str, true)} that match the condition (${conditions[0]})`;
                }
            },
            CanContainChildObjects: true,
            CanContainMultipleChildObjects: false,
            FriendlyName: "Percent of Child Responses"
        }
    }
}
